import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Download, Mail, Save } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useI18n } from "../i18n/I18nContext";
import ActivitySelect from "../components/ActivitySelect";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CompanyProfile() {
  const { t, lang } = useI18n();
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/company").then((r) => setC(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/company", {
        legal_name: c.legal_name, address: c.address, website: c.website,
        contact_person_name: c.contact_person_name, contact_person_role: c.contact_person_role,
        contact_phone: c.contact_phone, industry: c.industry, activity_code: c.activity_code, division_code: c.division_code, notes: c.notes, description: c.description,
      });
      setC(data);
      toast.success(t("profile.saved"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/company/generate-description", { lang });
      setC((prev) => ({ ...prev, description: data.description }));
      toast.success(t("profile.generated"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setGenerating(false); }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/company/send-email");
      toast.success(t("profile.sentTo", { email: data.to }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (!c) return <div className="p-12 text-muted-foreground" data-testid="profile-loading">{t("common.loading")}</div>;

  const field = (id, label) => (
    <div>
      <Label htmlFor={id} className="text-midnight text-sm">{label}</Label>
      <Input id={id} value={c[id] || ""} onChange={set(id)} data-testid={`profile-${id}-input`}
        className="mt-1.5 rounded-none border-sand focus-visible:ring-gold" />
    </div>
  );

  return (
    <div className="p-8 lg:p-12 max-w-5xl" data-testid="company-profile-page">
      <div className="mb-8">
        <p className="overline text-gold">{t("profile.overline")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-midnight mt-2 tracking-tight">{t("profile.title")}</h1>
        <p className="text-muted-foreground mt-2">{t("profile.subtitle")}</p>
      </div>

      <div className="border border-sand bg-white p-6 lg:p-8 space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          {field("legal_name", t("profile.legalName"))}
          {field("industry", t("profile.industry"))}
          {field("address", t("profile.address"))}
          {field("website", t("profile.website"))}
          {field("contact_person_name", t("profile.refName"))}
          {field("contact_person_role", t("profile.role"))}
          {field("contact_phone", t("profile.phone"))}
        </div>
        <ActivitySelect value={c.activity_code} onChange={(v) => setC({ ...c, activity_code: v, division_code: "" })}
          divisionValue={c.division_code} onDivisionChange={(v) => setC({ ...c, division_code: v })}
          testId="profile-activity-select" divisionTestId="profile-division-select" />
        <div>
          <Label htmlFor="notes" className="text-midnight text-sm">{t("profile.notesLabel")}</Label>
          <Textarea id="notes" value={c.notes || ""} onChange={set("notes")} data-testid="profile-notes-input"
            placeholder={t("profile.notesPh")}
            className="mt-1.5 rounded-none border-sand focus-visible:ring-gold min-h-[80px]" />
        </div>
      </div>

      <div className="mt-6 border border-sand bg-white p-6 lg:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="font-serif text-xl text-midnight">{t("profile.descTitle")}</h2>
            <p className="text-sm text-muted-foreground">{t("profile.descSub")}</p>
          </div>
          <Button onClick={generate} disabled={generating} data-testid="generate-description-btn"
            className="rounded-none bg-midnight text-bone hover:bg-midnight/90 transition-all duration-300">
            <Sparkles className="h-4 w-4 me-2" strokeWidth={1.5} /> {generating ? t("profile.generating") : t("profile.generateAI")}
          </Button>
        </div>
        <Textarea value={c.description || ""} onChange={set("description")} data-testid="profile-description-input"
          placeholder={t("profile.descPh")}
          className="rounded-none border-sand focus-visible:ring-gold min-h-[280px] font-sans text-sm leading-relaxed" />
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={save} disabled={saving} data-testid="save-profile-btn"
          className="rounded-none bg-gold text-midnight hover:brightness-105 transition-all duration-300">
          <Save className="h-4 w-4 me-2" strokeWidth={1.5} /> {saving ? t("profile.saving") : t("profile.save")}
        </Button>
        <a href={`${BACKEND}/api/company/pdf`} target="_blank" rel="noopener noreferrer" data-testid="download-profile-pdf-btn">
          <Button variant="outline" className="rounded-none border-midnight text-midnight hover:bg-midnight hover:text-bone transition-all duration-300">
            <Download className="h-4 w-4 me-2" strokeWidth={1.5} /> {t("profile.downloadPdf")}
          </Button>
        </a>
        <Button onClick={sendEmail} disabled={sending} variant="outline" data-testid="send-profile-email-btn"
          className="rounded-none border-midnight text-midnight hover:bg-midnight hover:text-bone transition-all duration-300">
          <Mail className="h-4 w-4 me-2" strokeWidth={1.5} /> {sending ? t("profile.sending") : t("profile.sendEmail")}
        </Button>
      </div>
    </div>
  );
}
